import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Phone, Calendar, MessageSquare } from 'lucide-react';
import { Loan } from '../data/mockLoans';
interface CallDispositionModalProps {
  isOpen: boolean;
  loan: Loan | null;
  onClose: () => void;
  onSubmit: (disposition: 'contacted' | 'no_answer' | 'voicemail', note: string, followUpTime: string) => void;
}
export function CallDispositionModal({
  isOpen,
  loan,
  onClose,
  onSubmit
}: CallDispositionModalProps) {
  const [disposition, setDisposition] = useState<'contacted' | 'no_answer' | 'voicemail' | null>(null);
  const [note, setNote] = useState('');
  const [followUpTime, setFollowUpTime] = useState('');
  useEffect(() => {
    if (isOpen) {
      setDisposition(null);
      setNote('');
      setFollowUpTime('');
    }
  }, [isOpen]);
  if (!isOpen || !loan) return null;
  const isFollowUpRequired = disposition === 'no_answer' || disposition === 'voicemail';
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (disposition && note.trim()) {
      if (isFollowUpRequired && !followUpTime) return;
      onSubmit(disposition, note, followUpTime);
    }
  };
  const isFormValid = disposition !== null && note.trim().length > 0 && (!isFollowUpRequired || followUpTime !== '');
  return <AnimatePresence data-id="element-45">
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{
      backgroundColor: 'rgba(42, 42, 42, 0.5)',
      backdropFilter: 'blur(4px)'
    }} data-id="element-46">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-stone-200" data-id="element-47">
          <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100 bg-stone-50/50" data-id="element-48">
            <h2 className="text-lg font-semibold text-stone-900 flex items-center gap-2" data-id="element-49">
              <Phone className="w-5 h-5" style={{
              color: '#C5A55A'
            }} data-id="element-50" />
              Log Call: {loan.borrowerName}
            </h2>
            <button onClick={onClose} className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-full transition-colors" data-id="element-51">
              <X className="w-5 h-5" data-id="element-52" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6" data-id="element-53">
            {/* Disposition */}
            <div data-id="element-54">
              <label className="block text-sm font-medium text-stone-700 mb-3" data-id="element-55">
                Call Disposition <span className="text-rose-500" data-id="element-56">*</span>
              </label>
              <div className="grid grid-cols-3 gap-3" data-id="element-57">
                {(['contacted', 'no_answer', 'voicemail'] as const).map(type => <button key={type} type="button" onClick={() => setDisposition(type)} className={`py-2.5 px-3 rounded-xl border text-sm font-medium transition-all ${disposition === type ? 'text-stone-900 ring-1' : 'bg-white border-stone-200 text-stone-600 hover:border-stone-300 hover:bg-stone-50'}`} style={disposition === type ? {
                backgroundColor: 'rgba(197, 165, 90, 0.1)',
                borderColor: '#C5A55A',
                boxShadow: '0 0 0 1px #C5A55A'
              } : {}} data-id="element-58">
                      {type === 'contacted' ? 'Contacted' : type === 'no_answer' ? 'No Answer' : 'Voicemail'}
                    </button>)}
              </div>
            </div>

            {/* Note */}
            <div data-id="element-59">
              <label className="block text-sm font-medium text-stone-700 mb-2 flex items-center gap-2" data-id="element-60">
                <MessageSquare className="w-4 h-4 text-stone-400" data-id="element-61" />
                Call Notes <span className="text-rose-500" data-id="element-62">*</span>
              </label>
              <textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Summarize the call or reason for voicemail..." className="w-full h-32 px-4 py-3 rounded-xl border border-stone-200 outline-none transition-all resize-none text-stone-700" onFocus={e => {
              e.currentTarget.style.borderColor = '#C5A55A';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(197, 165, 90, 0.15)';
            }} onBlur={e => {
              e.currentTarget.style.borderColor = '#d6d3d1';
              e.currentTarget.style.boxShadow = 'none';
            }} data-id="element-63" />
            </div>

            {/* Follow Up */}
            <div data-id="element-64">
              <label className="block text-sm font-medium text-stone-700 mb-2 flex items-center gap-2" data-id="element-65">
                <Calendar className="w-4 h-4 text-stone-400" data-id="element-66" />
                Schedule Follow-up
                {isFollowUpRequired ? <span className="text-rose-500" data-id="element-67">*</span> : <span className="text-stone-400 text-xs font-normal ml-1" data-id="element-68">
                    (optional)
                  </span>}
              </label>
              <input type="datetime-local" value={followUpTime} onChange={e => setFollowUpTime(e.target.value)} className="w-full px-4 py-2.5 rounded-xl border border-stone-200 outline-none transition-all text-stone-700" onFocus={e => {
              e.currentTarget.style.borderColor = '#C5A55A';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(197, 165, 90, 0.15)';
            }} onBlur={e => {
              e.currentTarget.style.borderColor = '#d6d3d1';
              e.currentTarget.style.boxShadow = 'none';
            }} data-id="element-69" />
              <p className="text-xs text-stone-500 mt-2" data-id="element-70">
                {isFollowUpRequired ? 'Required — the loan will re-appear in your queue at this time.' : 'Optional — schedule a follow-up if needed.'}
              </p>
            </div>

            <div className="pt-4 flex gap-3 border-t border-stone-100" data-id="element-71">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 px-4 rounded-xl border border-stone-200 text-stone-700 font-medium hover:bg-stone-50 transition-colors" data-id="element-72">
                Cancel
              </button>
              <button type="submit" disabled={!isFormValid} className="flex-1 py-2.5 px-4 rounded-xl text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed" style={{
              backgroundColor: '#C5A55A'
            }} onMouseEnter={e => {
              if (isFormValid) e.currentTarget.style.backgroundColor = '#B8963E';
            }} onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#C5A55A';
            }} data-id="element-73">
                Save & Next
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>;
}